<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { CustomContentCollateralEntity } from "@shared/services/event_tracking/entities";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";

  import type { CustomContentCollateral } from "@shared/types/CustomContent";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";

  export let collaterals: CustomContentCollateral[] = [];
  export let alignCollateralRight: boolean;
  export let mobileMode: boolean;
  export let displayIsVertical: boolean;
  export let useMargin: boolean = true;
  export let dataTestIdBase: string;

  const { trackClick } = EventTracking;

  let justifyEnd: boolean = alignCollateralRight && !mobileMode;
  // hides the margin for collateral section
  let shouldUseMargin: boolean = useMargin && mobileMode;
  let contentsDataTestId: string = dataTestIdBase;
  let testIdSuffix: string = "";

  $: {
    shouldUseMargin = useMargin && mobileMode;
    justifyEnd = alignCollateralRight && !mobileMode;
    if (displayIsVertical) {
      testIdSuffix = "-vertical";
    } else {
      testIdSuffix = justifyEnd ? "-right-aligned" : "-left-aligned";
    }
    contentsDataTestId = `${dataTestIdBase}-contents${testIdSuffix}`;
  }

  const collateralMissing = collateral => {
    return (collateral?.display_name || "X").split("").every(c => c === "X" || c === " ");
  };

  const getCollateralUrl = async collateral_id => {
    const options = {
      method: "GET",
      url: "/api/retailer/custom_content_collaterals/collateral_url",
      data: {
        collateral_id,
      },
    };

    return await HttpPromise(options).then(res => res.file_url);
  };

  const openCollateral = (collateral: CustomContentCollateral, entities: BaseEntityClass[]) => {
    const openCollateralForId = async () => {
      const collateralId = collateral.id;
      const collateralFileUrl = await getCollateralUrl(collateralId);
      const collateralEntity: CustomContentCollateralEntity = new CustomContentCollateralEntity({
        custom_content_collateral_id: collateral.id,
      });
      trackClick("custom_content_collateral", entities.concat(collateralEntity));
      // .focus only works if it's not a file that just gets downloaded (e.g. a zip file)
      window.open(collateralFileUrl, "_blank")?.focus();
    };

    return openCollateralForId;
  };
</script>

{#if collaterals.length}
  <TrackingContext let:trackingContext>
    <div data-testid={dataTestIdBase}>
      <div class:fizz-stack-16={shouldUseMargin} />
      <div
        class:fizz-flex-row={!displayIsVertical}
        class:fizz-justify-end={justifyEnd}
        class:fizz-flow-4={displayIsVertical}
        data-testid={contentsDataTestId}
      >
        {#each collaterals as collateral}
          <a
            class="fizz-flex-row-0"
            data-testid="collateral"
            href={"#"}
            on:click|preventDefault={openCollateral(collateral, trackingContext.entities)}
            class:fizz-underline-none={collateralMissing(collateral)}
          >
            <Icon type="File" extraClassNames="fizz-inline-4" />
            <span
              class="fizz-text-brand"
              style="margin-top: 1px"
              class:custom-content-collateral-missing={collateralMissing(collateral)}
              data-testid={`${dataTestIdBase}${
                collateralMissing(collateral) ? "-missing" : "-present"
              }`}
            >
              {collateral.display_name}
            </span>
          </a>
        {/each}
      </div>
    </div>
  </TrackingContext>
{/if}
